import '../css/app.css'
import Layouts from '@/Layouts'
import {
  init,
  replayIntegration,
  browserTracingIntegration,
  captureMessage,
} from '@sentry/vue'
import i18n from '@/Plugins/i18n'
import { createInertiaApp, router, usePage } from '@inertiajs/vue3'
import Vapor from 'laravel-vapor'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import { createApp, h, defineAsyncComponent } from 'vue'
import { useInertiaError } from '@/composables/useInertiaError'
import { isDeployment, isProduction } from '../../vite.helpers'
import { createPinia } from 'pinia'
import {
  installHistoryPositionTracker,
  registerHistoryPositionAppCleanup,
} from '@/composables/historyPosition'

const historyPositions = installHistoryPositionTracker()
if (import.meta.hot) import.meta.hot.dispose(() => historyPositions.dispose())

// The Design System look (resources/css/design-system/theme.css) is switched by
// `data-ds-theme` on <html>. app.blade.php renders it on the first load; this
// keeps it in sync when the active company changes without a full reload, and
// between the sign-in pages (their own switch, see useDesignSystem) and the app.
router.on('navigate', (event) => {
  const { page } = event.detail
  document.documentElement.toggleAttribute(
    'data-ds-theme',
    page.props._features?.['design-system'] === true ||
      (String(page.component).startsWith('Auth/') &&
        page.props._designSystemAuthPages === true),
  )
})

// Inertia resets the scroll regions when the new page renders, which is the end
// of the request, not the start of it. Until then the old page stays where it
// was scrolled to, and the only sign that anything is happening is the 2px
// progress bar at the very top of the window - off the user's line of sight on
// a page they have scrolled down. Reset as soon as the visit starts instead, so
// the loading state sits where the user is looking. Inertia's own reset on
// arrival then has nothing left to do.
//
// preserveScroll visits keep their position. In its callback form the flag can
// only be resolved against the new page, so it counts as "preserve" here.
router.on('start', (event) => {
  if (event.detail.visit.preserveScroll) {
    return
  }

  document.querySelectorAll('[scroll-region]').forEach((region) => {
    region.scrollTop = 0
  })
})

const env = import.meta.env

Vapor.withBaseAssetUrl(env.VITE_VAPOR_ASSET_URL)

window.addEventListener('message', (event) => {
  if (event.origin !== window.location.origin) return
  if (event.data?.type !== 'filament:navigate') return
  const url = event.data.url
  if (typeof url !== 'string') return
  if (!url.startsWith('/') && !url.startsWith(window.location.origin)) return
  router.visit(url, { preserveScroll: true })
})

createInertiaApp({
  // Without this the loading bar keeps Inertia's default #29d, a blue that belongs to no part of
  // the Suite. Inertia interpolates the value straight into a CSS rule, so the binding's own
  // variable works and carries the right purple for each look on its own: today's #8354d3 in the
  // old one, --ds-action-brand under the design system.
  progress: { color: 'var(--color-primary)' },
  resolve: async (name) => {
    const page = await resolvePageComponent(
      `./Pages/${name}.vue`,
      import.meta.glob('./Pages/**/*.vue'),
    )

    const layout = page?.default?.props?.layout?.default
    if (layout) {
      page.default.layout =
        Layouts[typeof layout === 'function' ? layout() : layout]
    } else {
      page.default.layout = Layouts.Default
    }

    return page
  },

  async setup({ el, App, props, plugin }) {
    const Vue = createApp({
      render: () => h(App, props),
    })
    registerHistoryPositionAppCleanup(Vue, historyPositions)

    if (isDeployment(env)) {
      // Sentry Initialization
      init({
        dsn: env.VITE_SENTRY_DSN,
        environment: env.VITE_ENV,
        tracePropagationTargets: [
          /^https:\/\/staging\.airlst\.app/,
          /^https:\/\/airlst\.app/,
        ],
        integrations: [browserTracingIntegration(), replayIntegration()],
        // Performance Monitoring
        tracesSampleRate: isProduction(env) ? 0.1 : 1.0,
        // Session Replay
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 0.05,
      })
    }

    const { show: showInertiaError } = useInertiaError()

    router.on('invalid', (event) => {
      const response = event.detail.response
      const status = response?.status

      if (!status || (status >= 500 && status < 600)) {
        event.preventDefault()

        // Table pages load their rows with GET requests, so only a failed GET
        // on a table page is a "too much data" situation. Any other failed
        // request on those pages (logout, bulk actions, deletes, ...) gets the
        // generic message (AIRLST-5584).
        const method = (response?.config?.method ?? 'get').toLowerCase()
        const currentComponent = usePage().component
        const tablePages = [
          '/List',
          '/Archived',
          '/Duplicates',
          '/GuestManagers',
          '/Logs',
          '/OrderList',
          'Dashboard',
        ]
        const isTablePage = tablePages.some((p) => currentComponent.endsWith(p))
        const isTable = method === 'get' && isTablePage

        if (isDeployment(env)) {
          captureMessage(
            `Inertia invalid response ${status} ${method.toUpperCase()} ${response?.config?.url}`,
          )
        }

        showInertiaError(isTable)
      }
    })

    Vue.component(
      'FontAwesomeIcon',
      // Libraries/icon.js renders the design-system icon set while the design-system look is on
      // and FontAwesome otherwise.
      defineAsyncComponent(() => import('@/Libraries/icon')),
    )
    Vue.use(createPinia())
    Vue.use(plugin).use(i18n).mount(el)
  },
})
